/**
 * FactureManuellePanel — Créer une facture manuelle + liste des factures émises.
 * Suit le mockup : templates de départ → formulaire (numéro auto, client, lignes)
 * → émission → Excel + PDF à en-tête. Onglet « Factures » de la brique compta.
 */
import { useEffect, useState } from 'react';
import { Card, Button, Alert, Field, Badge } from '@/components/ui';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { api, ApiException } from '@/lib/api';
import type { FactureManuelle, FactureTemplate, TiersRecherche } from '@/lib/api';
import { Plus, Trash2, FileSpreadsheet, FileText, FileCode, Send, CreditCard, CheckCircle2 } from 'lucide-react';

type LigneForm = { libelle: string; quantite: number; prixUnitaire: number };

const ORIGINE_LABEL: Record<string, string> = {
  CAU: 'Caution', REP: 'Réparation', LOC: 'Location', TEC: 'Technique', DIV: 'Divers',
};

function euro(n: number): string {
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

export function FactureManuellePanel() {
  const { confirm, dialogElement } = useConfirmDialog();
  const [templates, setTemplates] = useState<FactureTemplate[]>([]);
  const [factures, setFactures] = useState<FactureManuelle[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Formulaire
  const [origineCode, setOrigineCode] = useState('DIV');
  const [objet, setObjet] = useState('');
  const [clientNom, setClientNom] = useState('');
  const [clientAdresse, setClientAdresse] = useState('');
  const [dateEmission, setDateEmission] = useState(() => new Date().toISOString().slice(0, 10));
  const [dateEcheance, setDateEcheance] = useState('');
  const [lignes, setLignes] = useState<LigneForm[]>([{ libelle: '', quantite: 1, prixUnitaire: 0 }]);

  // Rattachement client : tiers de l'annuaire (recherche prédictive) ou saisie
  // libre enrichie (création automatique du tiers à l'émission).
  const [tiersId, setTiersId] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<TiersRecherche[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [clientEmail, setClientEmail] = useState('');
  const [clientSiret, setClientSiret] = useState('');
  const [clientCodePostal, setClientCodePostal] = useState('');
  const [clientVille, setClientVille] = useState('');
  const [clientTelephone, setClientTelephone] = useState('');
  const [creerTiers, setCreerTiers] = useState(true);
  // Facturation à une entité publique (Chorus / EN 16931).
  const [typeDestinataire, setTypeDestinataire] = useState<'particulier' | 'entreprise' | 'entreprise_sans_chorus' | 'public'>('particulier');
  const [numeroEngagement, setNumeroEngagement] = useState('');
  const [codeService, setCodeService] = useState('');

  // Filtre sur les factures émises (numéro / nom / montant / date)
  const [filtreFacture, setFiltreFacture] = useState('');

  async function charger() {
    try {
      const [t, f] = await Promise.all([api.comptabilite.facturesManuelles.templates(), api.comptabilite.facturesManuelles.list()]);
      setTemplates(t.templates); setFactures(f.factures);
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  useEffect(() => { void charger(); }, []);

  async function envoyer(id: string) {
    setError(null);
    try {
      const r = await api.comptabilite.facturesManuelles.envoyerEmail(id);
      const parts: string[] = [];
      // E-mail systématique.
      if (r.email?.sent) parts.push('Facture envoyée par e-mail');
      else if (r.email) parts.push(`E-mail non envoyé (${r.email.reason ?? 'pas d\'adresse'})`);
      // Canal officiel (Chorus/Hélios) en parallèle.
      if (r.efacture) {
        const suffixe = r.efacture.mock ? ' — mode test, dépôt simulé' : '';
        parts.push(`${r.efacture.message}${suffixe}`);
      } else if (r.efactureErreur) {
        parts.push(`Canal officiel : ${r.efactureErreur}`);
      }
      if (parts.length === 0) parts.push('Envoi effectué.');
      setInfo(parts.join(' · '));
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Envoi impossible.');
    }
  }

  async function payerEnLigne(id: string) {
    setError(null);
    try {
      const r = await api.comptabilite.facturesManuelles.payfip(id);
      // Mock (commune sans PayFiP actif) → on informe + propose le règlement manuel.
      if (r.mock) {
        const ok = await confirm({
          title: 'Paiement en ligne non activé',
          description:
            "PayFiP n'est pas activé pour cette commune (ou en mode test). Vous pouvez enregistrer un règlement reçu par chèque, espèces ou virement.",
          confirmLabel: 'Enregistrer le règlement',
          cancelLabel: 'Annuler',
        });
        if (ok) await marquerPaye(id);
        return;
      }
      // Réel → ouvre la page de paiement PayFiP.
      window.open(r.url, '_blank', 'noopener');
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Impossible de démarrer le paiement.');
    }
  }

  async function marquerPaye(id: string) {
    setError(null);
    const ok = await confirm({
      title: 'Enregistrer le règlement',
      description:
        'Marquer cette facture comme réglée créera un encaissement (chèque par défaut). Vous pourrez ajuster le moyen dans les encaissements.',
      confirmLabel: 'Marquer payée',
      cancelLabel: 'Annuler',
    });
    if (!ok) return;
    try {
      await api.comptabilite.facturesManuelles.marquerPaye(id, { moyen: 'cheque' });
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Impossible de marquer la facture payée.');
    }
  }

  // Recherche prédictive de tiers (débounce léger via l'appel direct onChange).
  async function onClientNomChange(v: string) {
    setClientNom(v);
    setTiersId(null); // toute frappe casse le rattachement précédent
    if (v.trim().length >= 2) {
      try {
        const r = await api.comptabilite.facturesManuelles.tiersRecherche(v.trim());
        setSuggestions(r.tiers);
        setShowSuggestions(r.tiers.length > 0);
      } catch { setSuggestions([]); setShowSuggestions(false); }
    } else {
      setSuggestions([]); setShowSuggestions(false);
    }
  }

  function choisirTiers(t: TiersRecherche) {
    setTiersId(t.id);
    setClientNom(t.nom);
    setClientAdresse(t.adresse ?? '');
    setClientEmail(t.email ?? '');
    setClientSiret(t.siret ?? '');
    setClientCodePostal(t.codePostal ?? '');
    setClientVille(t.ville ?? '');
    setClientTelephone(t.telephone ?? '');
    setShowSuggestions(false);
    setCreerTiers(false); // déjà en base, pas de recréation
  }

  function resetClient() {
    setTiersId(null); setClientNom(''); setClientAdresse(''); setClientEmail('');
    setClientSiret(''); setClientCodePostal(''); setClientVille(''); setClientTelephone('');
    setCreerTiers(true); setSuggestions([]); setShowSuggestions(false);
  }

  function appliquerTemplate(t: FactureTemplate) {
    setOrigineCode(t.origineCode);
    setObjet(t.objetDefaut ?? '');
    setLignes(t.lignesDefaut.length ? t.lignesDefaut.map((l) => ({ libelle: l.libelle, quantite: l.quantite, prixUnitaire: l.prixUnitaire })) : [{ libelle: '', quantite: 1, prixUnitaire: 0 }]);
  }

  const total = lignes.reduce((s, l) => s + l.quantite * l.prixUnitaire, 0);

  function majLigne(i: number, patch: Partial<LigneForm>) {
    setLignes((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }
  function ajouterLigne() { setLignes((ls) => [...ls, { libelle: '', quantite: 1, prixUnitaire: 0 }]); }
  function retirerLigne(i: number) { setLignes((ls) => ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls); }

  async function emettre() {
    setError(null);
    if (!clientNom.trim()) { setError('Indiquez le client.'); return; }
    if (!lignes.some((l) => l.libelle.trim())) { setError('Ajoutez au moins une prestation.'); return; }
    // Facturation publique : le SIRET du destinataire est requis (Chorus).
    if (typeDestinataire === 'public' && !clientSiret.trim()) {
      setError('Pour une entité publique, le SIRET du destinataire est obligatoire (Chorus).'); return;
    }
    setBusy(true);
    try {
      // Type du tiers dans l'annuaire, déduit du destinataire pour rester cohérent :
      //  particulier → bénéficiaire ; professionnel (Chorus ou non) → prestataire ;
      //  organisme public/État → autre.
      const clientTypeDeduit =
        typeDestinataire === 'particulier' ? 'beneficiaire'
        : typeDestinataire === 'public' ? 'autre'
        : 'prestataire'; // entreprise | entreprise_sans_chorus
      await api.comptabilite.facturesManuelles.creer({
        origineCode, objet: objet || undefined,
        tiersId: tiersId ?? null,
        clientNom: clientNom || null, clientAdresse: clientAdresse || null,
        clientEmail: clientEmail || null, clientSiret: clientSiret || null,
        clientCodePostal: clientCodePostal || null, clientVille: clientVille || null,
        clientTelephone: clientTelephone || null,
        clientType: clientTypeDeduit, clientCategorie: 'client',
        // Création auto seulement si saisie libre (pas déjà rattaché) et case cochée.
        creerTiers: !tiersId && creerTiers,
        dateEmission, dateEcheance: dateEcheance || null,
        typeDestinataire,
        numeroEngagement: typeDestinataire === 'public' ? (numeroEngagement || null) : null,
        codeService: typeDestinataire === 'public' ? (codeService || null) : null,
        lignes: lignes.filter((l) => l.libelle.trim()),
      });
      // reset
      setObjet(''); resetClient(); setDateEcheance('');
      setTypeDestinataire('particulier'); setNumeroEngagement(''); setCodeService('');
      setLignes([{ libelle: '', quantite: 1, prixUnitaire: 0 }]); setOrigineCode('DIV');
      await charger();
    } catch (e) {
      // Doublon de tiers → message clair (le maire choisit le tiers existant).
      setError(e instanceof ApiException ? e.message : 'Émission impossible');
    } finally { setBusy(false); }
  }

  return (
    <div className="space-y-5">
      {error && <Alert variant="error">{error}</Alert>}
      {info && <Alert variant="info">{info}</Alert>}

      {/* Templates de départ */}
      {templates.length > 0 && (
        <Card variant="glass" className="p-4">
          <h2 className="mb-1 font-display text-sm font-semibold text-ink">Partir d'un modèle</h2>
          <p className="mb-3 text-xs text-ink-muted">Le numéro se génère seul ; il ne reste qu'à changer le client et le montant.</p>
          <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))' }}>
            {templates.map((t) => (
              <button key={t.id} type="button" onClick={() => appliquerTemplate(t)}
                className="rounded-r-lg border border-border bg-surface/40 p-3 text-left transition hover:border-compta">
                <div className="text-sm font-medium text-ink">{t.libelle}</div>
                <div className="text-xs text-ink-subtle">Préfixe {t.origineCode}</div>
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Formulaire */}
      <Card variant="glass" padded>
        <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Field label="Origine (préfixe)" htmlFor="fm-origine">
            <select id="fm-origine" value={origineCode} onChange={(e) => setOrigineCode(e.target.value)}
              className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3 text-sm text-ink">
              {Object.entries(ORIGINE_LABEL).map(([k, v]) => <option key={k} value={k}>{v} ({k})</option>)}
            </select>
          </Field>
          <Field label="Date d'émission" htmlFor="fm-emission">
            <input id="fm-emission" type="date" value={dateEmission} onChange={(e) => setDateEmission(e.target.value)}
              className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3 text-sm text-ink" />
          </Field>
          <Field label="Échéance" htmlFor="fm-echeance">
            <input id="fm-echeance" type="date" value={dateEcheance} onChange={(e) => setDateEcheance(e.target.value)}
              className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3 text-sm text-ink" />
          </Field>
        </div>

        <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="relative">
            <label htmlFor="fm-client" className="mb-1 block text-xs font-medium text-ink-muted">Client / tiers</label>
            <input id="fm-client" type="text" value={clientNom} autoComplete="off"
              onChange={(e) => void onClientNomChange(e.target.value)}
              onFocus={() => { if (suggestions.length) setShowSuggestions(true); }}
              placeholder="Tapez un nom — recherche dans l'annuaire"
              className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3 text-sm text-ink" />
            {tiersId && (
              <span className="absolute right-3 top-9 text-xs font-medium text-success">✓ rattaché</span>
            )}
            {showSuggestions && suggestions.length > 0 && (
              <ul className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-r-lg border border-border bg-surface shadow-lg">
                {suggestions.map((t) => (
                  <li key={t.id}>
                    <button type="button" onClick={() => choisirTiers(t)}
                      className="flex w-full flex-col items-start px-3 py-2 text-left hover:bg-surface2/60">
                      <span className="text-sm font-medium text-ink">{t.nom}</span>
                      <span className="text-xs text-ink-subtle">
                        {t.email || 'sans e-mail'}{t.siret ? ` · SIRET ${t.siret}` : ''} · {t.categorie}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <Field label="Objet de la facture" htmlFor="fm-objet">
            <input id="fm-objet" type="text" value={objet} onChange={(e) => setObjet(e.target.value)}
              className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3 text-sm text-ink" />
          </Field>
        </div>

        {/* Coordonnées client : préremplies si tiers rattaché, sinon saisie libre
            qui crée le tiers dans l'annuaire à l'émission. */}
        <div className="rounded-r-lg border border-border/60 bg-surface/20 p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-semibold text-ink-muted">
              {tiersId ? 'Coordonnées (tiers rattaché — annuaire)' : 'Coordonnées du client (saisie libre)'}
            </span>
            {tiersId ? (
              <button type="button" onClick={resetClient} className="text-xs font-semibold text-compta">Changer de client</button>
            ) : (
              <label className="inline-flex items-center gap-1.5 text-xs text-ink-muted">
                <input type="checkbox" checked={creerTiers} onChange={(e) => setCreerTiers(e.target.checked)} />
                Créer dans l'annuaire
              </label>
            )}
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="fm-email" className="mb-1 block text-xs font-medium text-ink-muted">E-mail (pour l'envoi)</label>
              <input id="fm-email" type="email" value={clientEmail} disabled={!!tiersId}
                onChange={(e) => setClientEmail(e.target.value)}
                className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink disabled:opacity-60" placeholder="client@exemple.fr" />
            </div>
            <div>
              <label htmlFor="fm-siret" className="mb-1 block text-xs font-medium text-ink-muted">SIRET (si professionnel)</label>
              <input id="fm-siret" type="text" value={clientSiret} disabled={!!tiersId}
                onChange={(e) => setClientSiret(e.target.value)}
                className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink disabled:opacity-60" placeholder="14 chiffres" />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="fm-adr" className="mb-1 block text-xs font-medium text-ink-muted">Adresse</label>
              <input id="fm-adr" type="text" value={clientAdresse} disabled={!!tiersId}
                onChange={(e) => setClientAdresse(e.target.value)}
                className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink disabled:opacity-60" />
            </div>
            <div>
              <label htmlFor="fm-cp" className="mb-1 block text-xs font-medium text-ink-muted">Code postal</label>
              <input id="fm-cp" type="text" value={clientCodePostal} disabled={!!tiersId}
                onChange={(e) => setClientCodePostal(e.target.value)}
                className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink disabled:opacity-60" />
            </div>
            <div>
              <label htmlFor="fm-ville" className="mb-1 block text-xs font-medium text-ink-muted">Ville</label>
              <input id="fm-ville" type="text" value={clientVille} disabled={!!tiersId}
                onChange={(e) => setClientVille(e.target.value)}
                className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink disabled:opacity-60" />
            </div>
          </div>
        </div>

        {/* Destinataire — menu unique qui pilote le canal d'envoi et les champs publics */}
        <div className="mt-4 rounded-lg border border-border bg-surface/40 p-3">
          <label htmlFor="fm-destinataire" className="mb-1 block text-xs font-medium text-ink-muted">Destinataire de la facture</label>
          <select id="fm-destinataire" value={typeDestinataire}
            onChange={(e) => setTypeDestinataire(e.target.value as typeof typeDestinataire)}
            className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink sm:max-w-md">
            <option value="particulier">Particulier</option>
            <option value="entreprise">Professionnel — via Chorus Pro</option>
            <option value="entreprise_sans_chorus">Professionnel — sans Chorus (e-mail seul)</option>
            <option value="public">Organisme public / État (Chorus)</option>
          </select>
          <p className="mt-1.5 text-xs text-ink-subtle">
            {typeDestinataire === 'particulier'
              ? 'À l\'envoi : e-mail au client + titre de recette (Hélios).'
              : typeDestinataire === 'entreprise'
                ? 'À l\'envoi : e-mail au client + dépôt sur Chorus Pro.'
                : typeDestinataire === 'entreprise_sans_chorus'
                  ? 'À l\'envoi : e-mail au client uniquement (ce professionnel n\'utilise pas Chorus).'
                  : 'À l\'envoi : e-mail au client + dépôt sur Chorus Pro (obligatoire pour l\'État/public).'}
          </p>
          {typeDestinataire === 'public' && (
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2 text-xs text-ink-subtle">
                Pour facturer l'État ou un organisme public, Chorus exige ces informations
                (fournies par l'administration cliente sur son bon de commande).
              </div>
              <div>
                <label htmlFor="fm-engagement" className="mb-1 block text-xs font-medium text-ink-muted">
                  Numéro d'engagement {`(obligatoire pour l'État)`}
                </label>
                <input id="fm-engagement" type="text" value={numeroEngagement}
                  onChange={(e) => setNumeroEngagement(e.target.value)}
                  className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink"
                  placeholder="ex. EJ-2026-00456" />
              </div>
              <div>
                <label htmlFor="fm-codeservice" className="mb-1 block text-xs font-medium text-ink-muted">
                  Code service destinataire
                </label>
                <input id="fm-codeservice" type="text" value={codeService}
                  onChange={(e) => setCodeService(e.target.value)}
                  className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink"
                  placeholder="ex. DIRFIN" />
              </div>
            </div>
          )}
        </div>

        {/* Lignes */}
        <div className="mt-4">
          <div className="mb-1 text-xs font-medium text-ink-muted">Prestations</div>
          <div className="overflow-hidden rounded-r-lg border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-xs text-ink-subtle">
                  <th className="px-3 py-2 text-left font-medium">Libellé</th>
                  <th className="px-3 py-2 text-right font-medium" style={{ width: 70 }}>Qté</th>
                  <th className="px-3 py-2 text-right font-medium" style={{ width: 110 }}>P.U. €</th>
                  <th className="px-3 py-2 text-right font-medium" style={{ width: 120 }}>Montant</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {lignes.map((l, i) => (
                  <tr key={i} className="border-b border-border/60">
                    <td className="px-2 py-1.5">
                      <input type="text" value={l.libelle} onChange={(e) => majLigne(i, { libelle: e.target.value })}
                        className="h-9 w-full rounded-r-md border border-border bg-surface/40 px-2 text-sm text-ink" />
                    </td>
                    <td className="px-2 py-1.5">
                      <input type="number" min={0} step="0.5" value={l.quantite} onChange={(e) => majLigne(i, { quantite: parseFloat(e.target.value) || 0 })}
                        className="h-9 w-full rounded-r-md border border-border bg-surface/40 px-2 text-right text-sm text-ink" />
                    </td>
                    <td className="px-2 py-1.5">
                      <input type="number" min={0} step="0.01" value={l.prixUnitaire} onChange={(e) => majLigne(i, { prixUnitaire: parseFloat(e.target.value) || 0 })}
                        className="h-9 w-full rounded-r-md border border-border bg-surface/40 px-2 text-right text-sm text-ink" />
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-ink">{euro(l.quantite * l.prixUnitaire)}</td>
                    <td className="px-2 py-1.5 text-center">
                      <button type="button" onClick={() => retirerLigne(i)} className="text-ink-subtle hover:text-danger"><Trash2 className="h-4 w-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button type="button" onClick={ajouterLigne} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-compta">
            <Plus className="h-3.5 w-3.5" /> Ajouter une ligne
          </button>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <span className="text-xs text-ink-subtle">TVA non applicable, art. 293 B du CGI</span>
          <div className="text-sm"><span className="text-ink-muted">Total TTC </span><strong className="text-lg tabular-nums">{euro(total)}</strong></div>
        </div>

        <div className="mt-4 flex justify-end gap-2 border-t border-border pt-4">
          <Button variant="compta" disabled={busy} onClick={() => void emettre()}>{busy ? 'Émission…' : 'Émettre la facture'}</Button>
        </div>
      </Card>

      {/* Liste des factures émises */}
      <Card className="overflow-hidden p-0">
        <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
          <h2 className="font-display text-sm font-semibold text-ink">Factures émises</h2>
          <input type="text" value={filtreFacture} onChange={(e) => setFiltreFacture(e.target.value)}
            placeholder="Filtrer : n° · nom · montant · date"
            className="ml-auto h-9 w-64 rounded-r-md border border-border bg-surface/40 px-2 text-sm text-ink" />
        </div>
        {(() => {
          const q = filtreFacture.trim().toLowerCase();
          const filtrees = q
            ? factures.filter((f) =>
                f.numero.toLowerCase().includes(q) ||
                (f.clientNom ?? '').toLowerCase().includes(q) ||
                (f.objet ?? '').toLowerCase().includes(q) ||
                f.montantTtc.toFixed(2).includes(q) ||
                (f.dateEmission ?? '').includes(q))
            : factures;
          return filtrees.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-ink-subtle">
              {q ? 'Aucune facture ne correspond à la recherche.' : 'Aucune facture manuelle.'}
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {filtrees.map((f) => (
              <li key={f.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-ink">{f.numero}</span>
                    {f.origineCode && <Badge variant="info" size="sm">{ORIGINE_LABEL[f.origineCode] ?? f.origineCode}</Badge>}
                    <Badge variant={f.statut === 'payee' ? 'success' : 'warning'} size="sm">{f.statut}</Badge>
                  </div>
                  <div className="text-xs text-ink-subtle">{f.clientNom ?? '—'}{f.objet ? ` · ${f.objet}` : ''}</div>
                </div>
                <div className="text-right text-sm font-semibold tabular-nums text-ink">{euro(f.montantTtc)}</div>
                <div className="flex gap-1.5">
                  <button type="button" onClick={() => void api.comptabilite.facturesManuelles.telechargerExcel(f.id, f.numero)}
                    className="inline-flex items-center gap-1 rounded-r-md border border-border-hi px-2.5 py-1 text-xs text-ink-muted hover:text-ink" title="Excel éditable">
                    <FileSpreadsheet className="h-3.5 w-3.5" /> Excel
                  </button>
                  <button type="button" onClick={() => void api.comptabilite.facturesManuelles.telechargerPdf(f.id, f.numero)}
                    className="inline-flex items-center gap-1 rounded-r-md border border-border-hi px-2.5 py-1 text-xs text-ink-muted hover:text-ink" title="PDF à en-tête mairie">
                    <FileText className="h-3.5 w-3.5" /> PDF
                  </button>
                  {((f.typeDestinataire === 'public' || f.typeDestinataire === 'entreprise' || f.clientSiret) && f.typeDestinataire !== 'entreprise_sans_chorus') ? (
                    <button type="button" onClick={() => void api.comptabilite.facturesManuelles.telechargerFacturX(f.id, f.numero)}
                      className="inline-flex items-center gap-1 rounded-r-md border border-border-hi px-2.5 py-1 text-xs text-ink-muted hover:text-ink" title="Factur-X (PDF/A-3 déposé sur Chorus Pro)">
                      <FileText className="h-3.5 w-3.5" /> Factur-X
                    </button>
                  ) : null}
                  {f.typeDestinataire === 'particulier' ? (
                    <button type="button" onClick={() => void api.comptabilite.facturesManuelles.telechargerPes(f.id, f.numero)}
                      className="inline-flex items-center gap-1 rounded-r-md border border-border-hi px-2.5 py-1 text-xs text-ink-muted hover:text-ink" title="PES Aller (titre de recette pour Hélios — export de vérification)">
                      <FileCode className="h-3.5 w-3.5" /> PES
                    </button>
                  ) : null}
                  <button type="button" onClick={() => void envoyer(f.id)}
                    className="inline-flex items-center gap-1 rounded-r-md border border-border-hi px-2.5 py-1 text-xs text-ink-muted hover:text-ink"
                    title={
                      f.typeDestinataire === 'particulier'
                        ? 'Envoie la facture par e-mail ET émet le titre de recette (Hélios)'
                        : f.typeDestinataire === 'entreprise_sans_chorus'
                          ? 'Envoie la facture par e-mail'
                          : (f.typeDestinataire === 'public' || f.clientSiret)
                            ? 'Envoie la facture par e-mail ET la transmet sur Chorus Pro'
                          : 'Envoie la facture par e-mail'
                    }>
                    <Send className="h-3.5 w-3.5" /> Envoyer
                  </button>
                  {f.statut !== 'payee' && f.statut !== 'annulee' && (
                    <>
                      <button type="button" onClick={() => void payerEnLigne(f.id)}
                        className="inline-flex items-center gap-1 rounded-r-md border border-border-hi px-2.5 py-1 text-xs text-ink-muted hover:text-ink" title="Paiement en ligne PayFiP (si activé)">
                        <CreditCard className="h-3.5 w-3.5" /> Payer
                      </button>
                      <button type="button" onClick={() => void marquerPaye(f.id)}
                        className="inline-flex items-center gap-1 rounded-r-md border border-border-hi px-2.5 py-1 text-xs text-ink-muted hover:text-ink" title="Marquer réglée (chèque / espèces / virement)">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Marquer payée
                      </button>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
          );
        })()}
      </Card>
      {dialogElement}
    </div>
  );
}
